import React, { useState } from "react";
import {
  MagnifyingGlassIcon,
  VideoCameraIcon,
  MicrophoneIcon,
  DocumentIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Button from "../common/Button";

// ── Utility functions ──

export const PAGE_SIZE = 20;

export const formatDuration = (seconds: number) => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

export const formatSize = (bytes: number) => {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${(bytes / 1e3).toFixed(0)} KB`;
};

export const getFileTypeIcon = (fileType: string) => {
  switch (fileType) {
    case "video":
      return <VideoCameraIcon className="w-5 h-5 text-blue-500" />;
    case "audio":
      return <MicrophoneIcon className="w-5 h-5 text-purple-500" />;
    default:
      return <DocumentIcon className="w-5 h-5 text-base-content/60" />;
  }
};

// ── VideoPlayer ──

export const VideoPlayer: React.FC<{ src: string }> = ({ src }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 bg-gray-800 animate-pulse flex items-center justify-center">
          <VideoCameraIcon className="w-10 h-10 text-gray-500" />
        </div>
      )}
      <video
        controls
        autoPlay
        className={`max-w-full max-h-[70vh] transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
        src={src}
        onLoadedData={() => setLoaded(true)}
      >
        Your browser does not support the video tag.
      </video>
    </>
  );
};

// ── MediaPlayer (video/audio/unavailable with optional loading state) ──

interface MediaPlayerProps {
  src: string | null;
  loading?: boolean;
  isVideo?: boolean;
  loadingText?: string;
  unavailableText?: string;
}

export const MediaPlayer: React.FC<MediaPlayerProps> = ({
  src,
  loading = false,
  isVideo = false,
  loadingText = "Loading preview...",
  unavailableText = "Preview not available.",
}) => {
  return (
    <div className="flex-1 bg-gray-900 flex items-center justify-center min-h-0 relative">
      {loading ? (
        <div className="flex flex-col items-center gap-2">
          <span className="loading loading-spinner loading-md text-gray-400"></span>
          <p className="text-gray-400 text-sm">{loadingText}</p>
        </div>
      ) : src ? (
        isVideo ? (
          <VideoPlayer src={src} />
        ) : (
          <div className="p-8 w-full max-w-md">
            <audio controls autoPlay className="w-full" src={src}>
              Your browser does not support the audio tag.
            </audio>
          </div>
        )
      ) : (
        <div className="p-8 text-gray-400 text-sm">{unavailableText}</div>
      )}
    </div>
  );
};

// ── SearchBar ──

interface SearchBarProps {
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSearch: () => void;
  placeholder?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  searchInput,
  onSearchInputChange,
  onSearch,
  placeholder = "Search...",
}) => (
  <div className="flex gap-2 mb-4">
    <div className="relative flex-1">
      <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
      <input
        type="text"
        className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        placeholder={placeholder}
        value={searchInput}
        onChange={(e) => onSearchInputChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSearch()}
      />
    </div>
    <Button
      onClick={onSearch}
      variant="primary"
      size="sm"
      className="!min-h-0 h-[38px]"
    >
      Search
    </Button>
  </div>
);

// ── Pagination ──

interface PaginationProps {
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  totalCount,
  onPageChange,
}) => {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between pt-4 border-t border-base-300 mt-4">
      <p className="text-xs text-base-content/60">
        {totalCount.toLocaleString()} results
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 0}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        <span className="text-sm text-gray-700 dark:text-base-content/80">
          {page + 1} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages - 1}
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ── ModalListStates (loading / error / empty) ──

interface ModalListStatesProps {
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  emptyMessage?: string;
}

export const ModalListStates: React.FC<ModalListStatesProps> = ({
  loading,
  error,
  isEmpty,
  emptyMessage = "No results found.",
}) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md text-red-800 dark:text-red-400 text-sm">
        {error}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="text-center py-12 text-base-content/60">{emptyMessage}</div>
    );
  }

  return null;
};

// ── DetailViewShell (the detail view wrapper: header + content + footer) ──

interface DetailViewShellProps {
  onBack: () => void;
  onClose: () => void;
  title: string;
  headerMeta?: React.ReactNode;
  media: React.ReactNode;
  sidebar: React.ReactNode;
  footerLeft?: React.ReactNode;
  footerRight?: React.ReactNode;
}

export const DetailViewShell: React.FC<DetailViewShellProps> = ({
  onBack,
  onClose,
  title,
  headerMeta,
  media,
  sidebar,
  footerLeft,
  footerRight,
}) => (
  <div className="modal-box max-w-5xl max-h-[90vh] flex flex-col p-0">
    {/* Header */}
    <div className="flex items-center gap-3 px-6 py-4 border-b border-base-300">
      <button
        onClick={onBack}
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-base-200 transition-colors"
      >
        <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-base-content/70" />
      </button>
      <div className="flex-1 min-w-0">
        <h3 className="font-bold text-lg text-base-content truncate">{title}</h3>
        {headerMeta && (
          <div className="flex items-center gap-3 text-xs text-base-content/60">
            {headerMeta}
          </div>
        )}
      </div>
      <button onClick={onClose}>
        <XMarkIcon className="h-6 w-6" />
      </button>
    </div>

    {/* Content: media + sidebar */}
    <div className="flex-1 flex min-h-0">
      {media}
      <div className="w-72 border-l border-base-300 bg-base-100 flex flex-col">
        <div className="px-4 py-3 border-b border-base-300">
          <h4 className="text-sm font-semibold text-base-content">Details</h4>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{sidebar}</div>
      </div>
    </div>

    {/* Footer */}
    {(footerLeft || footerRight) && (
      <div className="flex items-center justify-between px-6 py-3 border-t border-base-300 bg-base-100">
        <div className="text-xs text-base-content/60">{footerLeft}</div>
        <div>{footerRight}</div>
      </div>
    )}
  </div>
);

// ── ListViewShell (the list view wrapper: header + search + content + pagination) ──

interface ListViewShellProps {
  title: string;
  subtitle: string;
  onClose: () => void;
  children: React.ReactNode;
}

export const ListViewShell: React.FC<ListViewShellProps> = ({
  title,
  subtitle,
  onClose,
  children,
}) => (
  <div className="modal-box max-w-4xl max-h-[85vh] flex flex-col">
    <div className="flex justify-between items-center mb-1">
      <h3 className="font-bold text-lg text-base-content">{title}</h3>
      <button onClick={onClose}>
        <XMarkIcon className="h-6 w-6" />
      </button>
    </div>
    <p className="text-sm text-base-content/60 mb-4">{subtitle}</p>
    {children}
  </div>
);

// ── DetailField (a single label + value field in the sidebar) ──

interface DetailFieldProps {
  label: string;
  children: React.ReactNode;
}

export const DetailField: React.FC<DetailFieldProps> = ({
  label,
  children,
}) => (
  <div>
    <p className="text-xs font-medium text-base-content/60 mb-1">{label}</p>
    {children}
  </div>
);

// ── ListItemButton (a single clickable row in the list) ──

interface ListItemButtonProps {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

export const ListItemButton: React.FC<ListItemButtonProps> = ({
  disabled = false,
  onClick,
  children,
}) => (
  <button
    onClick={() => !disabled && onClick()}
    disabled={disabled}
    className={`w-full text-left p-3 border rounded-lg transition-colors ${
      disabled
        ? "border-base-300 bg-base-200 opacity-60 cursor-not-allowed"
        : "border-base-300 hover:border-blue-300 dark:hover:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40"
    }`}
  >
    {children}
  </button>
);
